/**
 * The editor's text fields ⇄ a launch.json configuration.
 *
 * Arguments are one line, split like a shell would split them for display
 * purposes only: whitespace separates, "double" or 'single' quotes keep
 * spaces together. Nothing is ever handed to a shell — the result is an argv
 * array that Rust passes on as-is.
 *
 * Env is one `NAME=value` per line. Rust validates everything again on save.
 */

export function splitArgs(line) {
  const out = [];
  let cur = '';
  let quote = null;
  let has = false;
  const chars = Array.from(String(line || ''));
  for (let i = 0; i < chars.length; i += 1) {
    const ch = chars[i];
    if (quote === '"' && ch === '\\') {
      // Inside double quotes, the Windows command-line rule: backslashes are
      // literal (paths) unless a " follows them — then 2n of them are n
      // backslashes and the quote closes, 2n+1 are n plus a literal ".
      let n = 0;
      while (chars[i + n] === '\\') n += 1;
      if (chars[i + n] === '"') {
        cur += '\\'.repeat(Math.floor(n / 2));
        if (n % 2) {
          cur += '"';
          i += n;
        } else {
          i += n - 1;
        }
      } else {
        cur += '\\'.repeat(n);
        i += n - 1;
      }
    } else if (quote) {
      if (ch === quote) quote = null;
      else cur += ch;
    } else if (ch === '"' || ch === "'") {
      quote = ch;
      has = true;
    } else if (/\s/.test(ch)) {
      if (has || cur) out.push(cur);
      cur = '';
      has = false;
    } else {
      cur += ch;
    }
  }
  if (has || cur) out.push(cur);
  return out;
}

/** The inverse of splitArgs: `splitArgs(joinArgs(a))` deep-equals `a` for any argv. */
export function joinArgs(args) {
  const quote = (a) =>
    // Double the backslashes that would otherwise escape: those before a " and at the end.
    `"${a.replace(/(\\*)("|$)/g, (m, slashes, q) => slashes + slashes + (q ? '\\"' : ''))}"`;
  return (args || []).map((a) => (a === '' || /[\s"']/.test(a) ? quote(a) : a)).join(' ');
}

export function parseEnv(text) {
  const env = {};
  String(text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .forEach((l) => {
      const i = l.indexOf('=');
      if (i <= 0) env[l] = '';
      else env[l.slice(0, i).trim()] = l.slice(i + 1);
    });
  return env;
}

export function formatEnv(env) {
  return Object.entries(env || {})
    .map(([k, v]) => `${k}=${v}`)
    .join('\n');
}

/** Configuration → the editor's draft (all strings). */
export function toDraft(config) {
  return {
    name: config?.name || '',
    runtimeExecutable: config?.runtimeExecutable || '',
    args: joinArgs(config?.runtimeArgs),
    port: config?.port ? String(config.port) : '',
    cwd: config?.cwd || '',
    env: formatEnv(config?.env),
  };
}

const ENV_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * Draft → configuration, or `{ error }` naming the first bad field (an i18n
 * key under `projects.run.editor.errors`). `taken` = names other entries use.
 */
export function fromDraft(draft, taken = []) {
  const name = draft.name.trim();
  if (!name) return { error: 'nameRequired' };
  if (taken.includes(name)) return { error: 'nameTaken' };
  const runtimeExecutable = draft.runtimeExecutable.trim();
  if (!runtimeExecutable) return { error: 'commandRequired' };
  let port;
  if (draft.port.trim()) {
    port = Number(draft.port.trim());
    if (!Number.isInteger(port) || port < 1 || port > 65535) return { error: 'badPort' };
  }
  const cwd = draft.cwd.trim();
  if (/^([a-zA-Z]:|[\\/])/.test(cwd) || cwd.split(/[\\/]/).includes('..')) return { error: 'badCwd' };
  const env = parseEnv(draft.env);
  if (Object.keys(env).some((k) => !ENV_NAME.test(k))) return { error: 'badEnv' };
  const config = { name, runtimeExecutable, runtimeArgs: splitArgs(draft.args) };
  if (port) config.port = port;
  if (cwd && cwd !== '.') config.cwd = cwd;
  if (Object.keys(env).length) config.env = env;
  return { config };
}
